import { Tooltip } from '@/components/ui/tooltip';
import { useMe } from '@/hooks/use-me';
import { cls } from '@/utils/cls';
import type { Metric } from '@/warranty-v3/entities/metric';
import { formatMetricValue } from '@/warranty-v3/utils/format-metric-value';

export type MetricItemProps = {
  metric: Metric;
};

export function MetricItem({ metric }: MetricItemProps) {
  const me = useMe();
  const { unit } = metric;

  const getSohLabel = (metric: Metric) => {
    if (me.config.id === '77' && metric.id === 'availability') return '';
    if (me.config.id === '80') return 'lowest of Blocks';
    return 'avg.';
  };

  const isBelowGuaranteed =
    metric.guaranteedValue !== undefined &&
    metric.value < metric.guaranteedValue.effective;

  const showTooltip = metric.guaranteedValue
    ? metric.guaranteedValue.penalty > 0
    : false;

  return (
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-600">{metric.name}</div>
        <div
          className={cls('text-2xl font-bold', {
            'text-red-600 dark:text-red-400': isBelowGuaranteed,
          })}
        >
          {formatMetricValue(metric.value, unit)}{' '}
          <span className="text-xs font-normal text-gray-500">
            {getSohLabel(metric)}
          </span>
        </div>
      </div>
      <div className="text-right">
        <div className="text-xs text-gray-500">Guaranteed</div>
        {metric.guaranteedValue ? (
          <Tooltip
            hide={!showTooltip}
            clickToKeepOpen
            content={
              <table className="w-full font-mono text-sm">
                <tbody>
                  <tr>
                    <td className="w-0 pr-2 text-right">Base:</td>
                    <td className="w-0">
                      {formatMetricValue(
                        metric.guaranteedValue.base,
                        unit,
                        0,
                        5,
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="w-0 pr-2 text-right">Penalty:</td>
                    <td className="w-0">
                      {formatMetricValue(
                        -1 * metric.guaranteedValue.penalty,
                        unit,
                        0,
                        5,
                      )}
                    </td>
                  </tr>
                  <tr className="border-t border-gray-200">
                    <td className="w-0 pr-2 text-right"></td>
                    <td className="w-0 font-semibold">
                      {formatMetricValue(
                        metric.guaranteedValue.effective,
                        unit,
                        0,
                        5,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            }
          >
            <div
              className={cls('text-lg font-semibold text-gray-600', {
                'cursor-help': showTooltip,
              })}
            >
              {formatMetricValue(metric.guaranteedValue.effective, unit)}
            </div>
          </Tooltip>
        ) : (
          <div className="text-lg font-semibold text-gray-600">-</div>
        )}
      </div>
    </div>
  );
}
